import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from 'sonner';
import { User, Mail, Calendar, Shield, Key } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';
import Header from '@/components/Header';
import MinimalFooter from '@/components/MinimalFooter';

export default function MyProfile() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const getAuthProvider = () => {
    if (user.app_metadata.provider === 'google') {
      return 'Google';
    }
    return 'Email/Password';
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header onStartTour={() => {}} />
      
      <main className="container mx-auto px-4 py-8 max-w-4xl flex-grow">
        <div className="space-y-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Profile</h1>
            <p className="text-muted-foreground">
              Manage your account settings and preferences
            </p>
          </div>

          <div className="grid gap-6">
            {/* Account Information Card */}
            <Card>
              <CardHeader>
                <CardTitle>Account Information</CardTitle>
                <CardDescription>
                  Your basic account details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{user.email}</p>
                    <p className="text-sm text-muted-foreground">
                      Member since {format(new Date(user.created_at), 'MMMM yyyy')}
                    </p>
                  </div>
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Email verified:</span>
                    <span className={user.email_confirmed_at ? "text-green-500" : "text-yellow-500"}>
                      {user.email_confirmed_at ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Shield className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Last sign in:</span>
                    <span>
                      {format(new Date(user.last_sign_in_at || ''), 'MMM d, yyyy')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Key className="h-4 w-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Sign in method:</span>
                    <span className="capitalize">{getAuthProvider()}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Beta Access Card */}
            <Card>
              <CardHeader>
                <CardTitle>Beta Access</CardTitle>
                <CardDescription>
                  Your beta program status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">
                    You are currently participating in the BinauralFlow beta program
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <MinimalFooter />
    </div>
  );
} 